/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { IconTile } from '@atlaskit/icon';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import VideoIcon from '@atlaskit/icon/core/video';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type ExternalChapter,
	type SearchResultVideoPartial,
} from '@atlassian/dynamic-cards-search-client';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	getIntegrationKey,
	isNonPublic,
	processExcerpt,
	truncateDescription,
} from '../../../../../../../common/utils/search-result-utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../../controllers/store';

import { messages } from './messages';
import { convertVideoDuration, createChapterUrl, isLoomUrlExpired } from './utils';

interface LoomSearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultVideoPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

const styles = cssMap({
	thumbnailContainer: {
		position: 'relative',
		gridArea: 'body',
	},
	bodyArea: {
		display: 'flex',
		columnGap: token('space.150'),
	},
	footerContainer: {
		paddingTop: token('space.100'),
		position: 'relative',
	},
	dropdownItem: {
		display: 'flex',
		columnGap: token('space.100'),
		paddingBlock: token('space.050'),
		marginTop: token('space.100'),
	},
	seeAll: {
		textDecoration: 'none',
		marginTop: token('space.150'),
		font: token('font.body.UNSAFE_small'),
	},
	thumbnailImageStyles: {
		borderRadius: token('radius.small'),
		maxHeight: '6.5rem',
		width: 'auto',
	},
	chapterLink: {
		color: token('color.link'),
		textDecoration: 'none',
		'&:hover': {
			color: token('color.link'),
			textDecoration: 'underline',
		},
		'&:active': {
			textDecoration: 'none',
		},
		'&:visited': {
			color: token('color.link.visited'),
		},
		//@ts-expect-error
		'&:visited:active': {
			color: token('color.link.visited.pressed'),
		},
	},
});

const VideoThumbnail = ({ thumbnailUrl, url }: { thumbnailUrl?: string; url: string }) => {
	const { formatMessage } = useIntl();
	const [isErrored, setIsErrored] = useState(false);

	return !thumbnailUrl || isLoomUrlExpired(thumbnailUrl) || isErrored ? null : (
		<Inline testId="thumbnail-container" xcss={styles.thumbnailContainer}>
			<Anchor href={url} rel={'noreferrer'} target={'_blank'}>
				<img
					css={styles.thumbnailImageStyles}
					src={thumbnailUrl}
					alt={formatMessage(messages.videoThumbnailAltText)}
					onError={() => setIsErrored(true)}
				/>
			</Anchor>
		</Inline>
	);
};

const VideoChapters = ({ chapters, url }: { chapters?: ExternalChapter[]; url: string }) => {
	if (!chapters || chapters.length === 0) {
		return null;
	}

	return (
		<Box xcss={styles.footerContainer}>
			<DropdownMenu chapters={chapters} url={url}></DropdownMenu>
		</Box>
	);
};

const DropdownMenu = ({ chapters, url }: { chapters: ExternalChapter[]; url: string }) => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const dropdownMessage = formatMessage(messages.dropdownMessage, {
		chapterCount: chapters.length,
	});

	return (
		<Box>
			<Button
				iconAfter={(iconProps) =>
					isExpanded ? (
						<ChevronDownIcon {...iconProps} size="small" />
					) : (
						<ChevronRightIcon {...iconProps} size="small" />
					)
				}
				onClick={() => setIsExpanded(!isExpanded)}
				isSelected={isExpanded}
			>
				{dropdownMessage}
			</Button>
			{isExpanded && <DropdownItems chapters={chapters} url={url} />}
		</Box>
	);
};

const DropdownItems = ({ chapters, url }: { chapters: ExternalChapter[]; url: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Box>
			{chapters.map(({ title, startTimeInSeconds }) => {
				return (
					<Box xcss={styles.dropdownItem}>
						<IconTile
							size="16"
							icon={VideoIcon}
							label={formatMessage(messages.videoIconLabel)}
							appearance="blue"
						/>
						<Inline space={'space.050'}>
							<Text size="small" weight="medium">
								<Anchor
									xcss={styles.chapterLink}
									href={createChapterUrl(url, startTimeInSeconds)}
									rel={'noreferrer'}
									target="_blank"
								>
									{convertVideoDuration(startTimeInSeconds)}
								</Anchor>
							</Text>
							<Text size="small">{title}</Text>
						</Inline>
					</Box>
				);
			})}
			{chapters.length > 10 && (
				<Box xcss={styles.seeAll}>
					<Anchor href={url} target="_blank" xcss={styles.seeAll}>
						See all
					</Anchor>
				</Box>
			)}
		</Box>
	);
};

export const VideoSearchResult = ({ partial, position, icon }: LoomSearchResultProps) => {
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { lastModifiedDate, url, permissionLevel, entity } = partial;
	const { durationInSeconds, chapters, createdBy, thumbnailUrl } = entity || {};
	const creatorName = createdBy?.user?.name || createdBy?.thirdPartyUser?.name || null;

	let { title, excerpt } = partial;

	const excerptToDisplay = truncateDescription(excerpt || '', 300);
	const integrationKey = getIntegrationKey(partial.integrationId);

	const videoDuration = convertVideoDuration(durationInSeconds, { readable: true });

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const body = (
		<Box testId="thumbnail-container" xcss={styles.bodyArea}>
			<VideoThumbnail thumbnailUrl={thumbnailUrl} url={url} />
			<Box>{processExcerpt(excerptToDisplay, true)}</Box>
		</Box>
	);

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	return (
		<SearchResult
			id={partial.id}
			description={body}
			icon={icon}
			headerMetadata={
				<SearchMetadata
					product={integrationKey}
					productSelected={!!selectedProduct}
					parts={[
						'Video',
						['creator-name', creatorName],
						['last-modified', lastModifiedComponent],
						['video-duration', videoDuration],
						['padlock', padlockIcon],
					]}
				/>
			}
			footer={<VideoChapters chapters={chapters} url={url} />}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
